import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useQuery, usePreventWindowUnload } from '@panel/commons/hooks';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useGoogleMapsBootstrap } from '@panel/commons';
import { site } from '@panel/commons/helpers';
import { WrapperContainer } from '../../Wrappers/WrapperContainer';
import { sendEvent, PUBLICATION_ABANDON, track } from '../../../utils/trackingEvent';
import { step1Module2Schema } from './validations';
import { Step1Module2Selectors, Step1Module2Operations } from '../../../ducks/step1Module2';
import { Step1Module1Selectors } from '../../../ducks/step1Module1';
import { SavePostingOperations } from '../../../ducks/savePosting';
import { isNotEmptyNull } from '../../../utils/collections';
import { LocationSection } from './LocationSection';
import { MapSection } from './MapSection';
import { module2BodyManager } from '../../../utils/bodyManager';
import { useStepperCtx } from '../../stepper';
import { updateLastStep } from '../../../ducks/stepper';
import { formatCep } from './utils';

const validateDistrict = ['ZPAR', 'IWBR', 'WIBR', 'ICBR'].includes(site.id);

const Step1Module2 = WrapperContainer(({ setId, loading }) => {
  const { hasLocation, hasValues, isCompleted, formValues, isEdited, cep } = useSelector(
    Step1Module2Selectors.getStep1Module2State
  );
  const {
    postingStatuses,
    operationTypeId,
    propertyTypeId,
    subPropertyTypeId,
    propertyTypes,
    propertySubTypes
  } = useSelector(Step1Module1Selectors.getInfoEvents);
  const { setPreventTransition, nextStepperRoute } = useStepperCtx();
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const dispatch = useDispatch();
  const { districtData } = formValues;
  const { register, handleSubmit, reset, errors, setValue, getValues, clearErrors, formState, trigger } = useForm({
    defaultValues: {
      formValues
    },
    mode: 'onSubmit',
    shouldFocusError: true,
    resolver: yupResolver(step1Module2Schema),
    context: {
      validateDistrict: !validateDistrict && isNotEmptyNull(districtData)
    }
  });
  const formRef = useRef(null);
  const { isLoaded, loadError } = useGoogleMapsBootstrap({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY
  });

  const notifyAbandon = () => {
    if (postingStatuses.includes('DRAFT')) {
      track(PUBLICATION_ABANDON, operationTypeId, postingId);
    }
  };

  useEffect(() => {
    const formattedCep = formatCep(cep);
    if (formattedCep) {
      reset({ cep: formattedCep });
    }
  }, [cep, reset]);

  useEffect(() => {
    if (isEdited || formState.isDirty) {
      setPreventTransition(formRef);
    } else {
      setPreventTransition(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdited, formState]);

  useEffect(() => {
    setId('step1Module2Form');
  }, [setId]);

  useEffect(() => {
    window.addEventListener('beforeunload', notifyAbandon);
    return () => {
      window.removeEventListener('beforeunload', notifyAbandon);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingStatuses]);

  useEffect(() => {
    return () => dispatch(Step1Module2Operations.resetInfo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (postingId && !hasLocation && !hasValues) {
      const payload = {
        module: 'ADDRESS',
        step: 'STEP_MAIN',
        postingId
      };
      dispatch(Step1Module2Operations.getFormValues(payload));
    }
  }, [dispatch, hasLocation, hasValues, postingId]);

  useEffect(() => {
    if (isCompleted && !hasValues) {
      dispatch(Step1Module2Operations.setEditValues(hasLocation));
    }
  }, [dispatch, hasLocation, hasValues, isCompleted]);

  const onSubmit = (data, e) => {
    dispatch(Step1Module2Operations.setIsEdited(true));
    const payload = {
      body: { ...module2BodyManager(data), postingId },
      step: 'STEP_MAIN',
      module: 'ADDRESS'
    };
    const { nativeEvent } = e;
    const saveAndExit = nativeEvent.submitter && nativeEvent.submitter.id === 'saveAndExit';
    const savedFromModal = nativeEvent.detail && nativeEvent.detail.submitter === 'saveAndRedirect';
    const goBack = nativeEvent.detail && nativeEvent.detail.goBack;
    const path = goBack || nextStepperRoute || '/publicador/principales/caracteristicas';

    const propertyObj = { list: propertyTypes, id: propertyTypeId };
    const subPropertyObj = { list: propertySubTypes, id: subPropertyTypeId };

    sendEvent(
      'Principales Modulo2',
      saveAndExit ? 'Guardar y salir' : 'Continuar',
      operationTypeId,
      propertyObj,
      subPropertyObj
    );

    if (saveAndExit) {
      notifyAbandon();
    }

    dispatch(SavePostingOperations.savePostingDB(payload, path, saveAndExit, savedFromModal));

    dispatch(updateLastStep('1', '2'));
  };

  usePreventWindowUnload(formState.isDirty);

  return (
    <form data-repa='Step1Modulo2-form' id='step1Module2Form' ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <fieldset disabled={loading} data-repa='Step1Modulo2-form-fieldset'>
        <LocationSection
          register={register}
          errors={errors}
          trigger={trigger}
          isLoaded={isLoaded}
          reset={reset}
          loadError={loadError}
          clearErrors={clearErrors}
          setValue={setValue}
          getValues={getValues}
        />
        <MapSection register={register} isLoaded={isLoaded} loadError={loadError} />
      </fieldset>
    </form>
  );
});

export { Step1Module2 };
